<template>
	<div>
		<v-overlay
			v-model="dialog"
			:persistent="true"
			class="dimmer-overlay"
		></v-overlay>

		<v-menu
			ref="metadataSelectDialog"
			v-model="dialog"
			content-class="metadata-select-dialog"
			:persistent="true"
			:close-on-content-click="true"
			:min-width="minWidth"
			:min-height="minHeight"
			transition="false"
		>
			<template #activator="{ props }">
				<div>
					<slot v-bind="props" name="activator"></slot>
				</div>
			</template>
			<div class="menu-container">
				<div class="triangle" :style="triangleStyle"></div>
				<v-metadata-preview-card
					v-for="displayDatum in displayData"
					:key="displayDatum.dataPointIndex"
					:display-datum="displayDatum"
					:header-row="headerRow"
					class="hover-card"
					@click="onSelect(displayDatum.dataPointIndex)"
				/>
			</div>
		</v-menu>
	</div>
</template>

<script>
import { useTheme } from "vuetify";
import createCompositeKeyUtils from "@/compositeKeyUtils";
import VMetadataPreviewCard from "@/components/VMetadataPreview/VMetadataPreviewCard.vue";

export default {
	name: "VMetadataSelectDialog",

	components: {
		VMetadataPreviewCard,
	},

	props: {
		bounds: { type: Object, default: null },
	},

	setup() {
		const theme = useTheme();
		return { theme };
	},

	data() {
		return {
			compositeKeyData: [],
			data: {},
			dialog: false,
			displayData: [],
			headerRow: {
				axisLabel: "Axis",
				tagNameLabel: "Tag",
				valueLabel: "Value",
			},
			position: { x: 0, y: 0 },
			offset: 12,
			minWidth: 300,
			minHeight: 100,
			width: 300,
			height: 100,
			resolve: null,
			rowData: [],
			tagGroupName: "",
			triangleStyle: {
				position: "absolute",
				width: "0",
				height: "0",
				border: "8px solid transparent",
			},
		};
	},

	computed: {
		borderColor() {
			return this.theme.current.value.colors.secondary;
		},
	},

	methods: {
		async open(args, tags) {
			this.compositeKeyUtils = createCompositeKeyUtils(tags);
			this.position = args.position;
			this.data = args.data;

			if (!args.data) {
				return;
			}

			this.displayData = [];

			for (const datum of this.data) {
				const {
					index,
					pixel: { metadata },
				} = datum;

				this.displayData.push({
					dataPointIndex: index,
					rowData: Object.keys(metadata)
						.filter((key) => !isNaN(key) && !metadata[key].hidden)
						.map((key) => {
							return {
								tagName: metadata[key].tag.name,
								value: metadata[key].value,
								axis: metadata[key].axis,
							};
						}),
					tagGroupName: metadata.tagGroup.name,
					compositeKeyData: this.compositeKeyUtils.parseCompositeKeyWithLabels(
						metadata.compositeKey,
					),
				});
			}

			this.dialog = true;

			await this.$nextTick();
			const element = document.getElementsByClassName(
				"metadata-select-dialog",
			)[0];

			const arrowColor = this.theme.current.value.colors.secondary;
			if (element) {
				const y = this.position.y + this.bounds.top;
				const x = this.position.x + this.bounds.left;

				const viewportBounds = element.parentElement.getBoundingClientRect();
				const viewportMaxY = viewportBounds.height;
				const viewportMaxX = viewportBounds.width;
				const menuWidth = element.getBoundingClientRect().width;
				const menuHeight = element.getBoundingClientRect().height;
				this.height = menuHeight;
				this.width = menuWidth;

				const direction = this.determineDirection(x, y);

				// update triangle style based on direction
				switch (direction) {
					case "north":
						this.triangleStyle = {
							...this.triangleStyle,
							bottom: "-8px",
							left: "50%",
							transform: "translateX(-50%)",
							borderTopColor: arrowColor,
							borderLeft: "8px solid transparent",
							borderRight: "8px solid transparent",
							borderBottom: "none",
						};
						element.style.bottom = `${viewportMaxY - y + this.offset}px`;
						element.style.left = `${x - menuWidth / 2}px`;
						break;
					case "east":
						this.triangleStyle = {
							...this.triangleStyle,
							left: "-8px",
							top: "50%",
							transform: "translateY(-50%)",
							borderRightColor: arrowColor,
							borderTop: "8px solid transparent",
							borderBottom: "8px solid transparent",
							borderLeft: "none",
						};
						element.style.top = `${y - menuHeight / 2}px`;
						element.style.left = `${x + this.offset}px`;
						break;
					case "south":
						this.triangleStyle = {
							...this.triangleStyle,
							top: "-8px",
							left: "50%",
							transform: "translateX(-50%)",
							borderBottomColor: arrowColor,
							borderLeft: "8px solid transparent",
							borderRight: "8px solid transparent",
							borderTop: "none",
						};
						element.style.top = `${y + this.offset}px`;
						element.style.left = `${x - menuWidth / 2}px`;
						break;
					case "west":
						this.triangleStyle = {
							...this.triangleStyle,
							right: "-8px",
							top: "50%",
							transform: "translateY(-50%)",
							borderLeftColor: arrowColor,
							borderTop: "8px solid transparent",
							borderBottom: "8px solid transparent",
							borderRight: "none",
						};
						element.style.top = `${y - menuHeight / 2}px`;
						element.style.right = `${viewportMaxX - x + this.offset}px`;
						break;
				}
			}

			return new Promise((resolve) => {
				this.resolve = resolve;
			});
		},
		close() {
			this.dialog = false;
			const element = document.getElementsByClassName(
				"metadata-select-dialog",
			)[0];
			if (element) {
				element.style.top = "";
				element.style.left = "";
				element.style.right = "";
				element.style.bottom = "";
			}
			this.triangleStyle = {
				position: "absolute",
				width: "0",
				height: "0",
				border: "8px solid transparent",
			};
		},
		determineDirection(x, y) {
			let direction = "south";
			if (this.bounds.bottom - y < this.height) {
				direction = "north";
			}
			if (x - this.bounds.left < this.width / 2) {
				direction = "east";
			}
			if (this.bounds.right - x < this.width / 2) {
				direction = "west";
			}

			return direction;
		},
		hasAxisData(datum) {
			return datum.rowData.some((row) => !!row.axis);
		},
		onSelect(dataPointIndex) {
			this.resolve(dataPointIndex);
			this.close();
		},
	},
};
</script>

<style scoped lang="scss">
.menu-container {
	position: relative;
	display: flex;
}

.triangle {
	position: absolute;
	width: 0;
	height: 0;
	border: 8px solid transparent;
}

.border-bottom {
	position: relative;
}

.border-bottom::after {
	content: "";
	position: absolute;
	bottom: 0;
	left: 8px;
	right: 8px;
	border-bottom: 1px solid var(--border-default);
}

.dimmer-overlay {
	z-index: 1999; // Slightly lower than v-menu's default z-index
}

.hover-card {
	cursor: pointer;
	transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.hover-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}
</style>
